import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../../config/api';

const CreateAccount = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'student',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const { name, email, password, confirmPassword, role } = formData;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    // Validation
    if (!name || !email || !password || !confirmPassword) {
      setError('Please fill in all fields');
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/api/admin/create-account`, {
        name,
        email,
        password,
        role,
      });

      setSuccess(`Account created successfully! Student ID: ${response.data.studentId}`);
      setFormData({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        role: 'student',
      });

      // Auto-clear success message after 5 seconds
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return '#f44336';
      case 'teacher':
        return '#9c27b0';
      case 'grader':
        return '#ff9800';
      case 'student':
        return '#4caf50';
      default:
        return '#666';
    }
  };

  const getRoleDescription = (role) => {
    switch (role) {
      case 'admin':
        return 'Full administrative access, can manage tests, users, and system settings';
      case 'teacher':
        return 'Read-only access to monitor student progress and submissions';
      case 'grader':
        return 'Can grade student submissions and view all submissions';
      case 'student':
        return 'Can submit solutions to tests and view own grades';
      default:
        return '';
    }
  };

  return (
    <div className="container">
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
        <Link to="/admin" className="btn btn-secondary">
          ← Back to Admin Dashboard
        </Link>
        <h1 style={{ margin: 0 }}>👤 Create New Account</h1>
      </div>

      <div className="card" style={{ maxWidth: '600px', margin: '0 auto' }}>
        <h2 style={{ textAlign: 'center', marginBottom: '24px' }}>Manual Account Creation</h2>
        
        <div style={{ 
          padding: '16px', 
          backgroundColor: '#e7f3ff', 
          borderRadius: '8px', 
          border: '1px solid #b3d9ff',
          marginBottom: '24px'
        }}>
          <h4 style={{ margin: '0 0 8px 0', color: '#0066cc' }}>ℹ️ Administrator Account Creation</h4>
          <p style={{ margin: '0', color: '#0066cc', fontSize: '14px' }}>
            Use this form to manually create accounts for students, teachers, graders, or other administrators. 
            The user will receive the credentials you set here and should change their password upon first login.
          </p>
        </div>
        
        {error && (
          <div className="alert alert-error" style={{ marginBottom: '20px' }}>
            {error}
          </div>
        )}

        {success && (
          <div className="alert alert-success" style={{ marginBottom: '20px' }}>
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Full Name</label>
            <input
              type="text"
              name="name"
              value={name}
              onChange={handleChange}
              className="form-control"
              placeholder="Enter user's full name"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Email Address</label>
            <input
              type="email"
              name="email"
              value={email}
              onChange={handleChange}
              className="form-control"
              placeholder="Enter user's email address"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Role</label>
            <select
              name="role"
              value={role}
              onChange={handleChange}
              className="form-control"
              required
            >
              <option value="student">👩‍🎓 Student</option>
              <option value="teacher">👩‍🏫 Teacher</option>
              <option value="grader">🎓 Grader</option>
              <option value="admin">👨‍💼 Administrator</option>
            </select>
            
            <div style={{ 
              marginTop: '8px', 
              padding: '12px', 
              backgroundColor: '#f8f9fa', 
              borderRadius: '4px',
              border: `2px solid ${getRoleColor(role)}`
            }}>
              <div style={{ 
                fontWeight: 'bold', 
                color: getRoleColor(role),
                marginBottom: '4px'
              }}>
                {role.charAt(0).toUpperCase() + role.slice(1)} Role:
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>
                {getRoleDescription(role)}
              </div>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Initial Password</label>
            <input
              type="password"
              name="password"
              value={password}
              onChange={handleChange}
              className="form-control"
              placeholder="Set initial password for the user"
              required
            />
            <small style={{ color: '#666', fontSize: '12px' }}>
              Minimum 6 characters. User should change this on first login.
            </small>
          </div>

          <div className="form-group">
            <label className="form-label">Confirm Password</label>
            <input
              type="password"
              name="confirmPassword"
              value={confirmPassword}
              onChange={handleChange}
              className="form-control"
              placeholder="Confirm the initial password"
              required
            />
          </div>

          <div style={{ 
            padding: '16px', 
            backgroundColor: '#fff3cd', 
            borderRadius: '8px', 
            border: '1px solid #ffeaa7',
            marginBottom: '20px'
          }}>
            <h4 style={{ margin: '0 0 8px 0', color: '#856404' }}>🔒 Security Notice</h4>
            <ul style={{ margin: '0', color: '#856404', fontSize: '14px', paddingLeft: '20px' }}>
              <li>A unique Student ID will be automatically assigned</li>
              <li>The user should change their password upon first login</li>
              <li>Provide these credentials securely to the user</li>
              <li>Admin accounts have full system access - create responsibly</li>
            </ul>
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            style={{ width: '100%' }}
            disabled={loading}
          >
            {loading ? (
              <span>
                <div className="spinner" style={{ 
                  width: '16px', 
                  height: '16px', 
                  display: 'inline-block', 
                  marginRight: '8px' 
                }}></div>
                Creating Account...
              </span>
            ) : (
              'Create Account'
            )}
          </button>
        </form>

        <div style={{ marginTop: '24px', textAlign: 'center' }}>
          <p style={{ color: '#666' }}>
            Need to manage existing accounts?{' '}
            <Link to="/admin/users" style={{ color: '#007bff', textDecoration: 'none' }}>
              Go to User Management
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CreateAccount;
